/**
 * UnifiedAdaContextService
 *
 * Provides a single, consistent context for all Ask Ada interactions across the ASD UI.
 * This ensures that regardless of which page the user is on, Ask Ada has access to
 * the same comprehensive data about the enterprise infrastructure.
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: This service only handles context aggregation
 * - Open/Closed: New context sources can be added without modifying existing methods
 * - Dependency Inversion: Depends on canisterClient abstraction, not concrete implementations
 */

// ============================================================================
// Types
// ============================================================================

interface DeviceSummary {
    total: number;
    byType: Record<string, number>;
    byOS: Record<string, number>;
    byStatus: Record<string, number>;
}

interface PlatformInfo {
    name: string;
    assignments: number;
    primaryAssignments: number;
    estimatedAnnualCost: string;
    capabilities: string[];
}

interface HealthStatus {
    overall: 'GREEN' | 'YELLOW' | 'RED';
    serviceAvailability: string;
    securityPosture: string;
    risks: string[];
    recommendations: string[];
}

// ============================================================================
// UnifiedAdaContextService Class
// ============================================================================

export class UnifiedAdaContextService {
    private static instance: UnifiedAdaContextService | null = null;
    private cachedContext: string | null = null;
    private cacheTimestamp: number = 0;
    private readonly CACHE_TTL_MS = 30000; // 30 seconds cache

    /**
     * Singleton pattern - ensures consistent context across all Ask Ada instances
     */
    public static getInstance(): UnifiedAdaContextService {
        if (!UnifiedAdaContextService.instance) {
            UnifiedAdaContextService.instance = new UnifiedAdaContextService();
        }
        return UnifiedAdaContextService.instance;
    }

    private constructor() {
        console.log('UnifiedAdaContextService: Initialized');
    }

    /**
     * Convert metadata from vector_db tuple format to object.
     * vector_db returns metadata as Array<[string, string]>, not as an object.
     */
    private metadataToObject(metadataTuples: any): Record<string, string> {
        const obj: Record<string, string> = {};
        if (Array.isArray(metadataTuples)) {
            for (const tuple of metadataTuples) {
                if (Array.isArray(tuple) && tuple.length >= 2) {
                    obj[tuple[0]] = tuple[1];
                }
            }
        } else if (metadataTuples && typeof metadataTuples === 'object') {
            return metadataTuples;
        }
        return obj;
    }

    // ========================================================================
    // Main Public Method
    // ========================================================================

    /**
     * Build the full unified context for Ask Ada
     * This aggregates all data sources into a single comprehensive context
     */
    public async buildFullContext(): Promise<string> {
        // Check cache first
        const now = Date.now();
        if (this.cachedContext && (now - this.cacheTimestamp) < this.CACHE_TTL_MS) {
            console.log('UnifiedAdaContextService: Returning cached context');
            return this.cachedContext;
        }

        console.log('UnifiedAdaContextService: Building full context...');
        const startTime = Date.now();

        try {
            // Fetch all context sources in parallel for performance
            const [deviceContext, configServicesContext, capabilityContext, platformContext, healthContext, serviceMapContext, kbContext] =
                await Promise.all([
                    this.getDeviceContext(),
                    this.getConfigServicesContext(),
                    this.getCapabilityContext(),
                    this.getPlatformContext(),
                    this.getHealthContext(),
                    this.getServiceMapContext(),
                    this.getKnowledgeBaseContext()
                ]);

            // Get reference documents (static content)
            const referenceDocsContext = this.getReferenceDocumentsContext();

            const fullContext = `
=============================================================================
ENTERPRISE INFRASTRUCTURE CONTEXT - ASD (Agentic Service Discovery)
=============================================================================

${deviceContext}

${configServicesContext}

${capabilityContext}

${platformContext}

${serviceMapContext}

${healthContext}

${kbContext}

${referenceDocsContext}

=============================================================================
END OF CONTEXT
=============================================================================
`;

            // Cache the result
            this.cachedContext = fullContext;
            this.cacheTimestamp = now;

            const elapsed = Date.now() - startTime;
            console.log(`UnifiedAdaContextService: Full context built in ${elapsed}ms (${fullContext.length} chars)`);

            return fullContext;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error building context:', error);
            return this.getFallbackContext();
        }
    }

    /**
     * Force refresh the context cache
     */
    public invalidateCache(): void {
        this.cachedContext = null;
        this.cacheTimestamp = 0;
        console.log('UnifiedAdaContextService: Cache invalidated');
    }

    // ========================================================================
    // Context Source Methods
    // ========================================================================

    /**
     * Get device infrastructure context from vector_db
     */
    private async getDeviceContext(): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.getDeviceFallback();
            }

            // Fetch all devices for detailed info (statistics built from this data)
            const devicesResult = await canisterClient.getAllDevices();
            const devices = devicesResult?.ok || devicesResult || [];

            // Build summary
            const summary: DeviceSummary = {
                total: 0,
                byType: {},
                byOS: {},
                byStatus: {}
            };

            if (Array.isArray(devices)) {
                summary.total = devices.length;

                for (const device of devices) {
                    const metadata = this.metadataToObject(device[1]);

                    // Count by type
                    const deviceType = metadata.device_type || 'Unknown';
                    summary.byType[deviceType] = (summary.byType[deviceType] || 0) + 1;

                    // Count by OS
                    const os = metadata.os || 'Unknown';
                    summary.byOS[os] = (summary.byOS[os] || 0) + 1;

                    // Count by status
                    const status = metadata.status || 'Unknown';
                    summary.byStatus[status] = (summary.byStatus[status] || 0) + 1;
                }
            }

            // Format device list
            let deviceList = '';
            if (Array.isArray(devices) && devices.length > 0) {
                deviceList = '\nDevice Inventory:\n';
                for (const device of devices.slice(0, 20)) { // Limit to first 20
                    const id = device[0] || 'unknown';
                    const metadata = this.metadataToObject(device[1]);
                    const ip = metadata.ip || 'N/A';
                    const hostname = metadata.hostname || id;
                    const os = metadata.os || 'N/A';
                    const deviceType = metadata.device_type || 'N/A';
                    deviceList += `  - ${hostname} (${ip}) - ${deviceType}, ${os}\n`;
                }
                if (devices.length > 20) {
                    deviceList += `  ... and ${devices.length - 20} more devices\n`;
                }
            }

            return `
-----------------------------------------------------------------------------
DEVICE INFRASTRUCTURE
-----------------------------------------------------------------------------
Total Devices: ${summary.total}

By Type:
${Object.entries(summary.byType).map(([type, count]) => `  - ${type}: ${count}`).join('\n') || '  - No devices discovered'}

By Operating System:
${Object.entries(summary.byOS).map(([os, count]) => `  - ${os}: ${count}`).join('\n') || '  - No OS data available'}

By Status:
${Object.entries(summary.byStatus).map(([status, count]) => `  - ${status}: ${count}`).join('\n') || '  - No status data available'}
${deviceList}`;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error fetching device context:', error);
            return this.getDeviceFallback();
        }
    }

    private getDeviceFallback(): string {
        return `
-----------------------------------------------------------------------------
DEVICE INFRASTRUCTURE
-----------------------------------------------------------------------------
Device data unavailable - vector_db may not be accessible.
`;
    }

    /**
     * Get config files and services context from vector_db
     * This tells Ada what software/services are installed on each device
     */
    private async getConfigServicesContext(): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.getConfigServicesFallback();
            }

            // Fetch all configs
            const configsResult = await canisterClient.callCanister('vector_db', 'get_all_configs', []);
            const configs = configsResult || [];

            if (!Array.isArray(configs) || configs.length === 0) {
                return this.getConfigServicesFallback();
            }

            // Group configs by type and track which devices have each service
            const servicesByType: Record<string, Set<string>> = {};
            const configsByDevice: Record<string, string[]> = {};

            for (const config of configs) {
                // Config format: [config_id, ip, path, config_type]
                const ip = config[1] || '';
                const path = config[2] || '';
                const configType = config[3] || 'unknown';

                // Track by service type
                if (!servicesByType[configType]) {
                    servicesByType[configType] = new Set();
                }
                servicesByType[configType].add(ip);

                // Track configs per device
                if (!configsByDevice[ip]) {
                    configsByDevice[ip] = [];
                }
                configsByDevice[ip].push(path);
            }

            // Format services inventory
            let servicesInventory = 'Software/Services by Type:\n';
            const sortedTypes = Object.keys(servicesByType).sort();
            for (const serviceType of sortedTypes) {
                const devices = Array.from(servicesByType[serviceType]).sort();
                servicesInventory += `  - ${serviceType}: ${devices.length} device(s)\n`;
                servicesInventory += `    Devices: ${devices.join(', ')}\n`;
            }

            // Format device config summary
            let deviceConfigSummary = '\nConfig Files per Device:\n';
            const sortedIPs = Object.keys(configsByDevice).sort();
            for (const ip of sortedIPs) {
                const configPaths = configsByDevice[ip];
                // Extract service names from paths for summary
                const services = new Set<string>();
                for (const path of configPaths) {
                    if (path.includes('nginx')) services.add('nginx');
                    if (path.includes('postgresql') || path.includes('postgres')) services.add('postgresql');
                    if (path.includes('systemd')) services.add('systemd');
                    if (path.includes('netplan')) services.add('netplan');
                    if (path.includes('letsencrypt')) services.add('letsencrypt');
                    if (path.includes('apache')) services.add('apache');
                    if (path.includes('mysql')) services.add('mysql');
                    if (path.includes('redis')) services.add('redis');
                    if (path.includes('docker')) services.add('docker');
                    if (path.includes('kubernetes') || path.includes('k8s')) services.add('kubernetes');
                }
                const serviceList = Array.from(services).join(', ') || 'system configs';
                deviceConfigSummary += `  - ${ip}: ${configPaths.length} config(s) [${serviceList}]\n`;
            }

            return `
-----------------------------------------------------------------------------
INSTALLED SOFTWARE & SERVICES (from discovered config files)
-----------------------------------------------------------------------------
Total Config Files Discovered: ${configs.length}

${servicesInventory}
${deviceConfigSummary}
Note: Services are identified by analyzing discovered configuration files.
`;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error fetching config services context:', error);
            return this.getConfigServicesFallback();
        }
    }

    private getConfigServicesFallback(): string {
        return `
-----------------------------------------------------------------------------
INSTALLED SOFTWARE & SERVICES
-----------------------------------------------------------------------------
Config/services data unavailable - vector_db may not be accessible.
`;
    }

    /**
     * Get business capability context from vector_db
     */
    private async getCapabilityContext(): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.getCapabilityFallback();
            }

            // Try to get capability matrix from vector_db
            let matrix: any[] = [];
            try {
                const result = await canisterClient.callCanister('vector_db', 'get_capability_matrix', []);
                matrix = result?.ok || result || [];
            } catch (e) {
                // Capability matrix not available, use fallback
                return this.getCapabilityFallback();
            }

            // If no matrix data, use fallback
            if (!Array.isArray(matrix) || matrix.length === 0) {
                return this.getCapabilityFallback();
            }

            // Build detailed context
            let matrixDetails = '';
            let redundancies: string[] = [];
            const platformAssignments: Record<string, { total: number; primary: number; businessUnits: Set<string> }> = {
                'salesforce': { total: 0, primary: 0, businessUnits: new Set() },
                'servicenow': { total: 0, primary: 0, businessUnits: new Set() },
                'bmc': { total: 0, primary: 0, businessUnits: new Set() }
            };

            if (Array.isArray(matrix)) {
                matrixDetails = '\nCapability Matrix Detail:\n';
                matrixDetails += 'Business Unit | Capability | Platforms\n';
                matrixDetails += '-'.repeat(60) + '\n';

                for (const entry of matrix) {
                    const bu = entry.business_unit_name || 'Unknown';
                    const cap = entry.capability_name || 'Unknown';
                    const platforms = entry.platforms || [];

                    const platformNames = platforms.map((p: any) => {
                        const name = p.name || p.platform_id || 'Unknown';
                        const primary = p.is_primary ? ' (PRIMARY)' : '';

                        // Track platform usage
                        const platformKey = (p.platform_id || '').toLowerCase();
                        if (platformAssignments[platformKey]) {
                            platformAssignments[platformKey].total++;
                            if (p.is_primary) platformAssignments[platformKey].primary++;
                            platformAssignments[platformKey].businessUnits.add(bu);
                        }

                        return `${name}${primary}`;
                    }).join(', ');

                    matrixDetails += `${bu} | ${cap} | ${platformNames}\n`;

                    // Track redundancies (multiple platforms for same capability)
                    if (platforms.length > 1) {
                        redundancies.push(`${bu} - ${cap}: ${platforms.map((p: any) => p.name).join(', ')}`);
                    }
                }
            }

            // Compute statistics from matrix data
            const uniqueBusinessUnits = new Set<string>();
            const uniqueCapabilities = new Set<string>();
            matrix.forEach((entry: any) => {
                if (entry.business_unit_name) uniqueBusinessUnits.add(entry.business_unit_name);
                if (entry.capability_name) uniqueCapabilities.add(entry.capability_name);
            });

            const totalAssignments = platformAssignments.salesforce.total +
                                     platformAssignments.servicenow.total +
                                     platformAssignments.bmc.total;

            return `
-----------------------------------------------------------------------------
BUSINESS CAPABILITY MODEL
-----------------------------------------------------------------------------
Total Business Units: ${uniqueBusinessUnits.size || 'N/A'}
Total Capabilities: ${uniqueCapabilities.size || 6}
Total Platforms: 3 (Salesforce, ServiceNow, BMC)
Total Platform Assignments: ${totalAssignments}

Platform Usage Summary:
  - Salesforce: ${platformAssignments.salesforce.total} assignments (${platformAssignments.salesforce.primary} primary)
  - ServiceNow: ${platformAssignments.servicenow.total} assignments (${platformAssignments.servicenow.primary} primary)
  - BMC: ${platformAssignments.bmc.total} assignments (${platformAssignments.bmc.primary} primary)

${redundancies.length > 0 ? `
Platform Overlaps (Redundancies):
${redundancies.map(r => `  - ${r}`).join('\n')}
` : ''}
${matrixDetails}`;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error fetching capability context:', error);
            return this.getCapabilityFallback();
        }
    }

    private getCapabilityFallback(): string {
        return `
-----------------------------------------------------------------------------
BUSINESS CAPABILITY MODEL
-----------------------------------------------------------------------------
Capability data unavailable - vector_db may not be accessible.
`;
    }

    /**
     * Get platform consolidation context
     */
    private async getPlatformContext(): Promise<string> {
        // Platform cost data (would typically come from Flexera or similar)
        // Currently hardcoded as per CIO dashboard requirements
        const platforms: PlatformInfo[] = [
            {
                name: 'Salesforce',
                assignments: 23,
                primaryAssignments: 18,
                estimatedAnnualCost: '$3.2M',
                capabilities: ['CRM', 'Sales Automation', 'Customer Portal', 'Marketing Cloud']
            },
            {
                name: 'ServiceNow',
                assignments: 24,
                primaryAssignments: 16,
                estimatedAnnualCost: '$2.5M',
                capabilities: ['ITSM', 'Help Desk', 'HR Service Delivery', 'IT Operations Management']
            },
            {
                name: 'BMC',
                assignments: 11,
                primaryAssignments: 7,
                estimatedAnnualCost: '$1.2M',
                capabilities: ['Legacy ITSM', 'Helix CMDB', 'AIOps', 'Discovery']
            }
        ];

        const totalCost = '$6.9M';
        const potentialSavings = '$4.2M';

        return `
-----------------------------------------------------------------------------
ENTERPRISE PLATFORM INVENTORY
-----------------------------------------------------------------------------
Total Annual Platform Spend: ${totalCost}
Potential Consolidation Savings: ${potentialSavings} (if BMC decommissioned + Salesforce optimized)

Platform Details:

${platforms.map(p => `
${p.name}:
  - Total Assignments: ${p.assignments}
  - Primary Assignments: ${p.primaryAssignments}
  - Estimated Annual Cost: ${p.estimatedAnnualCost}
  - Key Capabilities: ${p.capabilities.join(', ')}
`).join('')}

Consolidation Opportunities:
  1. Sunset BMC & Migrate to ServiceNow ITOM
     - Impact: Reduce dual-ITSM complexity
     - Savings: ~$1.2M annually
     - Risk: Medium (requires migration planning)

  2. Optimize Salesforce Licenses
     - Impact: Right-size license allocation
     - Savings: ~$500K annually
     - Risk: Low (license audit)

  3. Consolidate Help Desk on ServiceNow
     - Impact: Single pane of glass for IT support
     - Savings: Operational efficiency gains
     - Risk: Low-Medium (change management)

License Overlap Alerts (Flexera):
  - BMC ↔ ServiceNow: Overlapping ITSM capabilities detected
  - Salesforce ↔ ServiceNow: Portal functionality overlap
`;
    }

    /**
     * Get service map context from vector_db
     */
    private async getServiceMapContext(): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.getServiceMapFallback();
            }

            // Try to get service map names first
            let serviceMapNames: string[] = [];
            try {
                const namesResult = await canisterClient.callCanister('vector_db', 'list_service_map_names', []);
                serviceMapNames = namesResult || [];
            } catch (e) {
                console.log('UnifiedAdaContextService: Could not fetch service map names');
            }

            if (!Array.isArray(serviceMapNames) || serviceMapNames.length === 0) {
                return this.getServiceMapFallback();
            }

            // Get details for each service map (include all, limit devices per tier)
            let mapDetails = '';
            for (const mapName of serviceMapNames) {
                try {
                    const mapResult = await canisterClient.callCanister('vector_db', 'get_service_map', [mapName]);
                    if (mapResult) {
                        const serviceMap = mapResult;
                        mapDetails += `\n  ${serviceMap.name || mapName}:\n`;
                        mapDetails += `    Service ID: ${serviceMap.service_id || 'N/A'}\n`;

                        // List devices by tier
                        if (serviceMap.tiers && Array.isArray(serviceMap.tiers)) {
                            for (const tier of serviceMap.tiers) {
                                const tierName = tier.tier_name || tier.name || 'Unknown Tier';
                                mapDetails += `    ${tierName}:\n`;
                                if (tier.devices && Array.isArray(tier.devices)) {
                                    for (const device of tier.devices.slice(0, 5)) {
                                        const hostname = device.hostname || device.device_id || 'unknown';
                                        const ip = device.ip_address || device.ip || 'N/A';
                                        const deviceType = device.device_type || 'N/A';
                                        mapDetails += `      - ${hostname} (${ip}) - ${deviceType}\n`;
                                    }
                                    if (tier.devices.length > 5) {
                                        mapDetails += `      ... and ${tier.devices.length - 5} more devices\n`;
                                    }
                                }
                            }
                        }
                    }
                } catch (e) {
                    mapDetails += `  - ${mapName} (details unavailable)\n`;
                }
            }

            return `
-----------------------------------------------------------------------------
SERVICE MAPS
-----------------------------------------------------------------------------
Total Service Maps: ${serviceMapNames.length}
${mapDetails}
Service maps show business service topology with servers and their relationships.
`;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error fetching service map context:', error);
            return this.getServiceMapFallback();
        }
    }

    private getServiceMapFallback(): string {
        return `
-----------------------------------------------------------------------------
SERVICE MAPS
-----------------------------------------------------------------------------
Available Service Maps:
  - BMC Software Platform
  - ServiceNow Platform
  - Salesforce Platform
  - Customer Management Service
  - IT Operations Service
`;
    }

    /**
     * Get health and risk status context
     */
    private async getHealthContext(): Promise<string> {
        // Health status (would typically come from monitoring systems)
        // Currently represents the dashboard static data
        const health: HealthStatus = {
            overall: 'GREEN',
            serviceAvailability: '99.9%',
            securityPosture: 'CAUTION - Slow Architecture Review',
            risks: [
                'Architectural Debt: Dual ITSM systems (BMC + ServiceNow) causing operational complexity',
                'Vendor Lock-in: Heavy Salesforce Cloud dependency with rising costs',
                'Manual Processes: Several IT operations still require manual intervention'
            ],
            recommendations: [
                'Sunset BMC & Migrate to ServiceNow ITOM (12-week timeline)',
                'Optimize Salesforce Licenses (8-week timeline)',
                'Implement Automated Discovery (ongoing)'
            ]
        };

        return `
-----------------------------------------------------------------------------
IT ENVIRONMENT HEALTH & RISK STATUS
-----------------------------------------------------------------------------
Overall Health: ${health.overall}
Service Availability: ${health.serviceAvailability}
Security Posture: ${health.securityPosture}

Identified Risks:
${health.risks.map((r, i) => `  ${i + 1}. ${r}`).join('\n')}

Recommended Actions (12-Week Outlook):
${health.recommendations.map((r, i) => `  ${i + 1}. ${r}`).join('\n')}

Decision Confidence: DATA-DRIVEN. ARCHITECTURE-ALIGNED. EXECUTION-READY.
`;
    }

    /**
     * Get Knowledge Base context from knowledge_base canister
     */
    private async getKnowledgeBaseContext(): Promise<string> {
        try {
            const canisterClient = (window as any).canisterClient;
            if (!canisterClient) {
                return this.getKBFallback();
            }

            // Fetch KB statistics and documents
            const [stats, documents] = await Promise.all([
                canisterClient.callCanister('knowledge_base', 'get_statistics', []),
                canisterClient.callCanister('knowledge_base', 'list_documents', [[]])
            ]);

            let kbContent = `
-----------------------------------------------------------------------------
KNOWLEDGE BASE
-----------------------------------------------------------------------------
`;

            // Add statistics
            if (stats) {
                kbContent += `Total Documents: ${stats.total_documents || 0}
  - Errors: ${stats.errors || 0}
  - Resolutions: ${stats.resolutions || 0}
  - Best Practices: ${stats.best_practices || 0}
  - Architecture Decision Records: ${stats.adrs || 0}

`;
            }

            // Add document summaries
            if (Array.isArray(documents) && documents.length > 0) {
                kbContent += 'Document Index:\n';

                // Group by type
                const errors = documents.filter((d: any) => d.doc_type?.Error !== undefined);
                const resolutions = documents.filter((d: any) => d.doc_type?.Resolution !== undefined);
                const bestPractices = documents.filter((d: any) => d.doc_type?.BestPractice !== undefined);
                const adrs = documents.filter((d: any) => d.doc_type?.ADR !== undefined);

                if (errors.length > 0) {
                    kbContent += '\nErrors:\n';
                    for (const doc of errors.slice(0, 10)) {
                        kbContent += `  - ${doc.id}: ${doc.title}\n`;
                        if (doc.summary) kbContent += `    Summary: ${doc.summary}\n`;
                    }
                }

                if (resolutions.length > 0) {
                    kbContent += '\nResolutions:\n';
                    for (const doc of resolutions.slice(0, 10)) {
                        kbContent += `  - ${doc.id}: ${doc.title}\n`;
                        if (doc.summary) kbContent += `    Summary: ${doc.summary}\n`;
                    }
                }

                if (bestPractices.length > 0) {
                    kbContent += '\nBest Practices:\n';
                    for (const doc of bestPractices.slice(0, 10)) {
                        kbContent += `  - ${doc.id}: ${doc.title}\n`;
                        if (doc.summary) kbContent += `    Summary: ${doc.summary}\n`;
                    }
                }

                if (adrs.length > 0) {
                    kbContent += '\nArchitecture Decision Records:\n';
                    for (const doc of adrs.slice(0, 10)) {
                        kbContent += `  - ${doc.id}: ${doc.title}\n`;
                        if (doc.summary) kbContent += `    Summary: ${doc.summary}\n`;
                    }
                }
            }

            return kbContent;

        } catch (error) {
            console.error('UnifiedAdaContextService: Error fetching KB context:', error);
            return this.getKBFallback();
        }
    }

    private getKBFallback(): string {
        return `
-----------------------------------------------------------------------------
KNOWLEDGE BASE
-----------------------------------------------------------------------------
Knowledge Base data unavailable - knowledge_base canister may not be accessible.
`;
    }

    /**
     * Get reference documents context (static strategic documents)
     */
    private getReferenceDocumentsContext(): string {
        return `
-----------------------------------------------------------------------------
REFERENCE DOCUMENTS
-----------------------------------------------------------------------------

== FLEXERA DEMO FLOW (ASD Demo for Flexera CTO) ==

Key Message: "From 12-Month Business Case to 12-Week Execution"

Demo Structure (25 minutes):
1. OPENING (2 min): Set the scene - CIO asks about consolidating Salesforce, BMC, ServiceNow
   - Old way: 6-12 month business case
   - ASD way: Days, not months

2. THE CONTROL PLANE (3 min): Dashboard showing platform consolidation scenario
   - Services discovered, mapped, costed, linked in real-time

3. LIVE DISCOVERY (5 min): 4-phase discovery workflow
   - Phase 1: Target selection (9 EC2 instances)
   - Phase 2: JIT credential checkout with auto-expiration
   - Phase 3: Active discovery (SSH/WinRM/SNMP)
   - Phase 4: Results stored with blockchain audit trail
   - Key: "Compliance built-in, not bolted-on"

4. ASK ADA - THE CIO'S QUESTION (5 min): Natural language queries
   - "Show me all systems related to BMC. What's the impact if we decommission?"
   - "Compare infrastructure footprint of Salesforce vs ServiceNow"

5. SERVICE MAP & BLAST RADIUS (3 min): Cytoscape visualization
   - Real dependency data, not guesses

6. EA HANDOFF (2 min): Business Capabilities view
   - Business Unit → Capability → Platform mapping

7. AUDIT TRAIL (2 min): Blockchain storage
   - Immutable records, cryptographic verification

Key Differentiators:
- Real-time, not static (Live discovery vs stale CMDBs)
- Compliance built-in (Blockchain audit trail)
- AI-powered analysis (Natural language queries)
- Air-gapped capable (Self-hosted LLM option)

Scorecard (ASD vs Traditional):
| Dimension           | Old Way        | With ASD         |
|---------------------|----------------|------------------|
| Time to decision    | 6-12 months    | Days to weeks    |
| Business case       | Manual         | Continuous, data-driven |
| CIO involvement     | Late           | First            |
| EA role             | Bottleneck     | Accelerator      |
| Risk confidence     | Assumed        | Proven           |


== CIO PLATFORM CONSOLIDATION STRATEGY ==

Executive Summary:
- Current Annual Spend: $20M - $40M
- Overlap Waste: $7.2M - $8.2M (29%)
- Savings Potential: $6.7M - $13M (25-35%)
- Payback Period: ~20 months
- 5-Year NPV: $25M - $50M

Platform Overview:
1. BMC Software: Enterprise IT Management & Automation
   - Helix ITSM, Helix Discovery, Helix Operations, Control-M, BMC AMI (Mainframe)

2. Salesforce: Customer Relationship Management
   - Sales Cloud, Service Cloud, Marketing Cloud, Commerce Cloud, Platform (Lightning)

3. ServiceNow: Enterprise Workflow & Service Management
   - ITSM, ITOM, CMDB, HRSD, CSM, SecOps, App Engine

Platform Overlap Analysis ($7.2M Annual Waste):
| Overlap Area              | Cost   | Priority    |
|---------------------------|--------|-------------|
| ServiceNow + BMC          | $4.8M  | HIGH        |
| ServiceNow + Salesforce   | $2.1M  | MEDIUM      |
| Salesforce + BMC          | $0.3M  | LOW         |

Recommended Target State:
- SALESFORCE: Keep & Expand (CRM, Sales, Marketing, Service Cloud)
- SERVICENOW: Keep & Expand (ITSM, ITOM/CMDB, HRSD, SecOps)
- BMC: PARTIAL - Keep Control-M & AMI only, Retire Helix products

Products to Retire:
- BMC Helix ITSM → ServiceNow ITSM
- BMC Helix Discovery → ServiceNow ITOM
- BMC TrueSight → ServiceNow ITOM
- ServiceNow CSM → Salesforce Service Cloud

Products to Keep:
- BMC Control-M (No equivalent, critical for batch processing)
- BMC AMI (Mainframe essential for banking core systems)

Implementation Phases:
- Phase 0 (Months 1-3): Discovery & Business Case - $300K-$500K
- Phase 1 (Months 4-6): Planning & Vendor Negotiation - $500K-$800K
- Phase 2 (Months 7-12): Foundation & Quick Wins - $3M-$5M
- Phase 3 (Months 13-21): Major Migrations - $4M-$7M
- Phase 4 (Months 22-24): Optimization & Steady State - $500K-$1M

Key Success Factors:
1. Executive sponsorship (CIO + CFO joint ownership)
2. Change management investment
3. Phased approach (quick wins first)
4. Parallel running during transitions
5. Vendor partnership engagement

Consolidation Recommendations:
1. Consolidate ITSM/CMDB/Discovery to ServiceNow (HIGH priority)
2. Consolidate customer-facing to Salesforce (HIGH priority)
3. Retain BMC Control-M and AMI (CRITICAL)
4. Implement unified integration layer (MEDIUM)
5. Phase implementation over 24 months (HIGH)
`;
    }

    /**
     * Fallback context when data sources are unavailable
     */
    private getFallbackContext(): string {
        return `
=============================================================================
ENTERPRISE INFRASTRUCTURE CONTEXT - ASD (Agentic Service Discovery)
=============================================================================

Data sources are currently unavailable. Limited context available.

Known Platforms:
  - Salesforce (CRM, Sales Automation)
  - ServiceNow (ITSM, Help Desk)
  - BMC (Legacy ITSM)

For accurate information, please ensure:
  1. The canister proxy is running (port 5001)
  2. The vector_db canister is deployed
  3. Discovery has been run to populate device data

=============================================================================
`;
    }
}

// ============================================================================
// Global Instance Export
// ============================================================================

// Create and export singleton instance
export const unifiedAdaContext = UnifiedAdaContextService.getInstance();

// Also attach to window for easy access from other modules
declare global {
    interface Window {
        unifiedAdaContext: UnifiedAdaContextService;
    }
}

window.unifiedAdaContext = unifiedAdaContext;

console.log('UnifiedAdaContextService: Module loaded');
